import './demos.less'

import {Icon} from '@codeurs/front'
import {Block, BlockType} from 'blocks/block'
import {Blocks} from 'blocks/blocks'
import React, {useEffect, useState} from 'react'
import {LinkBaseType, LinkType, PageData, WysiwygType} from 'types'
import {t} from 'util/i18n'
import {Link} from 'util/link'
import {style} from 'util/style'
import {DemosFilter} from 'views/demos/demosfilter'
import {FiltertagsItemType} from 'views/filter/filtertags'
import {Hero, HeroType} from 'views/hero/hero'
import {Layout} from 'views/layout'
import {Column, Grid} from 'views/layout/grid'
import {Theme} from 'views/theme'

const styles = style('demos', ['overview', 'item'])

export type DemosItemType = {
	title: string
	booth_label: string
	floorplan_id: string
	labels: Array<string>
	demo_labels: Array<{type: 'entry'; id: number} & LinkBaseType>
	content_title: string
	description: WysiwygType
	links: Array<LinkType>
}

export const Demos: React.FC<PageData & {
	hero: HeroType
	demos: Array<DemosItemType>
	blocks: Array<BlockType>
	disable_filters: boolean
	tags: Array<FiltertagsItemType>
}> = data => {
	const {title, hero, demos, blocks, disable_filters, tags} = data
	const [filters, setFilters] = useState([])
	let visibleDemos = [...demos]
	const filtersSet = new Set(filters)
	if (filters.length) {
		visibleDemos = visibleDemos
			.map(demo => {
				let count = 0
				demo.demo_labels?.forEach(label => {
					if (filtersSet.has(label.id.toString())) count++
					return
				})
				return {...demo, count}
			})
			.filter(demo => demo.count !== 0)
			.sort((a, b) => b.count - a.count)
	}

	return (
		<Layout page={data}>
			<div class={styles()}>
				<Hero pagetitle={title} {...hero} />
				{!disable_filters && (
					<DemosFilter tags={tags} filters={filters} setFilters={setFilters} />
				)}
				<DemosOverview demos={visibleDemos} />
				{blocks && blocks.length !== 0 && (
					<Blocks className={styles('blocks')}>
						{blocks.map((block, i) => (
							<Block key={i} {...block} />
						))}
					</Blocks>
				)}
			</div>
		</Layout>
	)
}

export const DemosOverview: React.FC<{
	demos: Array<DemosItemType>
}> = ({demos}) => {
	if (!demos || demos.length === 0)
		return (
			<div className={styles.overview('noresults')}>
				<Theme.OuterContainer>
					<Theme.Container>
						<Theme.H2>{t('filters.noresults.title')}</Theme.H2>
						<div className={styles.overview('noresults-text')}>
							<Theme.Wysiwyg>{t('filters.noresults.text')}</Theme.Wysiwyg>
						</div>
					</Theme.Container>
				</Theme.OuterContainer>
			</div>
		)

	return (
		<div className={styles.overview()}>
			<Theme.OuterContainer>
				<Theme.Container>
					<Grid columns={1} m={2} l={2} xl={2} xxl={2} gap={[30, 30]}>
						{demos.map((demo, i) => (
							<Column key={i}>
								<DemosItem {...demo} />
							</Column>
						))}
					</Grid>
				</Theme.Container>
			</Theme.OuterContainer>
		</div>
	)
}

export const DemosItem: React.FC<DemosItemType> = ({
	title,
	booth_label,
	floorplan_id,
	demo_labels,
	content_title,
	description,
	links
}) => {
	const [readmore, setReadmore] = useState('')

	useEffect(() => {
		const descriptionElement = document.createElement('div')
		descriptionElement.innerHTML = description
		const paragraphs = descriptionElement.querySelectorAll('p')
		setReadmore(paragraphs.length > 1 ? 'readmore' : '')
	}, [description])

	return (
		<div className={styles.item()}>
			<div className={styles.item('top')} />
			<header className={styles.item('header')}>
				{booth_label && (
					<div className={styles.item('header-index')}>
						{booth_label && <p>{booth_label}</p>}
					</div>
				)}
				<div className={styles.item('header-title')}>
					{content_title && <Theme.H4>{content_title}</Theme.H4>}
					{demo_labels &&
						demo_labels.length !== 0 &&
						demo_labels.map(tag => (
							<p className={styles.item('header-tag')}>{tag.title}</p>
						))}
				</div>
			</header>
			<div className={styles.item('content').mod(readmore)}>
				{description && (
					<div className={styles.item('content-description')}>
						<Theme.Wysiwyg>{description}</Theme.Wysiwyg>
					</div>
				)}
				<a
					className={styles.item('content-readmore')}
					onClick={() => setReadmore('')}
				>
					Read more...
				</a>
				{links && links.length !== 0 && (
					<div className={styles.item('content-links')}>
						{links.map((link, i) => {
							if (!link?.url) return null
							return (
								<div className={styles.item('content-links-item')} key={i}>
									<Link
										to={link.url}
										className={styles.item('content-links-item-link')}
									>
										{link.description || link.title}
									</Link>
									<span className={styles.item('content-links-item-icon')}>
										<Icon icon="arrow-right" />
									</span>
								</div>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}
